/**
 * Club Customization Shared Utilities
 *
 * This file contains utility functions shared between club-customization.js
 * and club-customization-2.js. It must be loaded BEFORE those scripts.
 */
(function() {
    'use strict';

    // ============================================================================
    // PRIVATE HELPERS
    // ============================================================================

    /**
     * Escape HTML attribute values to prevent breaking attributes with quotes
     * @private
     */
    function _escapeHtmlAttribute(str) {
        if (!str) return '';
        return String(str)
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    /** SVG icon for dropdown */
    const _dropdownSvg = `<svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

    // ============================================================================
    // PUTTER LENGTH UTILITIES
    // ============================================================================

    /**
     * Generate putter length range with 0.5 increments
     * @param {number} min - Minimum length
     * @param {number} max - Maximum length
     * @returns {Array} Array of length options
     */
    function generatePutterLengths(min, max) {
        const lengths = [];
        for (let i = min; i <= max; i += 0.5) {
            lengths.push(`${i}"`);
        }
        lengths.push('Custom (add in notes)');
        return lengths;
    }

    /**
     * Putter length ranges based on Putting Style
     */
    const putterLengths = {
        'standard': generatePutterLengths(28, 36),
        'counterbalanced': generatePutterLengths(36, 40),
        'broomstick': generatePutterLengths(40, 50),
        'armlock': generatePutterLengths(38, 46),
        'default': generatePutterLengths(32, 36) // Default fallback
    };

    // ============================================================================
    // LIE ANGLE UTILITIES
    // ============================================================================

    /**
     * Generate lie angle range with 1 degree increments
     * @param {number} min - Minimum angle
     * @param {number} max - Maximum angle
     * @returns {Array} Array of lie angle options
     */
    function generateLieAngles(min, max) {
        const angles = [];
        for (let i = min; i <= max; i += 1) {
            angles.push(`${i}°`);
        }
        // Add 79.5 if max is 79.5 (since loop increments by 1)
        if (max === 79.5) {
            angles.push('79.5°');
        }
        angles.push('Custom (add in notes)');
        return angles;
    }

    /**
     * Lie angle ranges based on Putting Style
     */
    const lieAngles = {
        'standard': generateLieAngles(63, 79.5),
        'counterbalanced': generateLieAngles(67, 75),
        'broomstick': generateLieAngles(77, 79.5),
        'armlock': generateLieAngles(68, 79.5),
        'default': generateLieAngles(68, 72) // Default fallback
    };

    // ============================================================================
    // DOM UTILITIES
    // ============================================================================

    /**
     * Populate select options from an array
     * @param {HTMLSelectElement} selectElement - The select element to populate
     * @param {Array} options - Array of option values
     * @param {boolean} preserveFirstOption - Whether to preserve the first option
     */
    function _populateSelectOptions(selectElement, options, preserveFirstOption = true) {
        if (!selectElement) return;

        let firstOptionHtml = '';
        if (preserveFirstOption) {
            const firstOption = selectElement.querySelector('option[value=""]');
            firstOptionHtml = firstOption ? firstOption.outerHTML : '<option value="">Select option</option>';
        }

        selectElement.innerHTML = firstOptionHtml;

        options.forEach(optionValue => {
            const option = document.createElement('option');
            option.value = optionValue;
            option.textContent = optionValue;
            selectElement.appendChild(option);
        });
    }

    // ============================================================================
    // DATA FETCHING UTILITIES
    // ============================================================================

    /**
     * Fetch products from collection feed with pagination support
     * @param {string} collectionHandle - The handle of the collection to fetch
     * @returns {Promise<{products: Array, vendors: Array}>}
     */
    async function fetchCollectionFeed(collectionHandle) {
        try {
            let allProducts = [];
            let allVendors = [];
            let currentPage = 1;
            let totalPages = 1;

            // Fetch all pages
            do {
                const response = await fetch(`/collections/${collectionHandle}?view=feed&page=${currentPage}`);

                if (!response.ok) {
                    throw new Error(`Failed to fetch collection feed: ${response.status}`);
                }

                const data = await response.json();

                // Accumulate products from this page
                allProducts = allProducts.concat(data.products);

                // Get vendors from first page (they're the same across all pages)
                if (currentPage === 1) {
                    allVendors = data.vendors || [];
                    totalPages = data.pagination?.total_pages || 1;
                }

                currentPage++;
            } while (currentPage <= totalPages);

            // Transform all products into the expected format
            const transformedProducts = allProducts.flatMap(product =>
                product.variants.map(variant => ({
                    title: variant.title === 'Default Title'
                        ? product.title
                        : `${product.title} - ${variant.title}`,
                    vendor: product.vendor,
                    variantId: variant.id,
                    price: parseFloat(variant.price) * 100, // Convert to cents
                    priceFormatted: new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD'
                    }).format(variant.price),
                    image: variant.metafields?.customization_thumbnail?.url ||
                           (product.images.find(img => img.url)?.url || product.featured_image?.url || ''),
                    flex: variant.metafields?.flex || null,
                    material: variant.metafields?.material || null
                }))
            );

            return {
                products: transformedProducts,
                vendors: allVendors.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
            };
        } catch (error) {
            console.error('Error loading collection feed:', error);
            return { products: [], vendors: [] };
        }
    }

    /**
     * Fetch variant override products from the feed
     * @param {string} productHandle - The product handle
     * @param {string|number} variantId - The variant ID
     * @returns {Promise<{override_shafts: Array|null, override_grips: Array|null}>}
     */
    async function fetchVariantOverrideProducts(productHandle, variantId) {
        if (!productHandle || !variantId) {
            return { override_shafts: null, override_grips: null };
        }

        try {
            const response = await fetch(`/products/${productHandle}?view=variant-override-feed&variant=${variantId}`);
            if (!response.ok) {
                console.warn('Failed to fetch variant override products');
                return { override_shafts: null, override_grips: null };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error fetching variant override products:', error);
            return { override_shafts: null, override_grips: null };
        }
    }

    /**
     * Convert variant metafield list to product format
     * @param {Array} variants - Array of variant objects from metafield
     * @returns {Array} - Array of products in the expected format
     */
    function convertVariantsToProducts(variants) {
        if (!variants || !Array.isArray(variants)) {
            return [];
        }

        return variants.map(variant => ({
            id: variant.id,
            variantId: variant.id,
            title: variant.product?.title
                ? (variant.title === 'Default Title' ? variant.product.title : `${variant.product.title} - ${variant.title}`)
                : (variant.title === 'Default Title' ? '' : variant.title),
            vendor: variant.product?.vendor || '',
            price: variant.price || 0,
            priceFormatted: new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format((variant.price || 0) / 100),
            image: variant.image || null,
            available: variant.available !== false,
            // For backward compatibility with existing code
            product: variant.product
        }));
    }

    // ============================================================================
    // MODEL SELECTOR
    // ============================================================================

    /**
     * Unified model selector for all product types (shaft, grip, adapter, ferrule, putter shaft)
     * @param {Object} config - Configuration object
     * @param {string} config.modalId - ID of the modal
     * @param {string} config.hiddenInputId - ID of hidden input for selected value
     * @param {string} config.variantIdInputId - ID of hidden input for variant ID
     * @param {string} config.selectButtonClass - Class of the select button
     * @param {string} config.optionsListId - ID of the options list container
     * @param {string} config.searchInputId - ID of the search input
     * @param {string} [config.brandSelectId] - ID of main form brand select
     * @param {string} [config.modalBrandSelectId] - ID of modal brand select
     * @param {string} config.optionClass - Class for option items
     * @param {string} [config.productsDataId] - ID of embedded JSON script (for adapter/ferrule)
     * @param {string} [config.windowDataKey] - Window key for dynamic data (for shaft/grip)
     * @param {Function} [config.getFilters] - Optional function returning extra filter values
     * @param {Function} [config.filterProduct] - Optional function for extra product filtering
     * @param {Function} [config.onFilterChange] - Optional callback when filters change
     */
    function initModelSelector(config) {
        const {
            modalId, hiddenInputId, variantIdInputId, selectButtonClass,
            optionsListId, searchInputId, brandSelectId, modalBrandSelectId,
            optionClass, productsDataId, windowDataKey, getFilters, filterProduct, onFilterChange
        } = config;

        const hiddenInput = document.getElementById(hiddenInputId);
        const selectButton = document.querySelector(`.${selectButtonClass}`);
        const optionsList = document.getElementById(optionsListId);
        const searchInput = document.getElementById(searchInputId);
        const brandSelect = document.getElementById(brandSelectId);
        const modalBrandSelect = document.getElementById(modalBrandSelectId);

        // Get products from either embedded JSON or window variable
        let products = [];
        if (windowDataKey && window[windowDataKey]) {
            products = window[windowDataKey];
        } else if (productsDataId) {
            const el = document.getElementById(productsDataId);
            if (el) {
                try { products = JSON.parse(el.textContent); } catch (e) { return; }
            }
        }
        if (!products.length || !optionsList) return;

        let selectedProduct = null;

        function clearSelection() {
            hiddenInput && (hiddenInput.value = '');
            const variantInput = document.getElementById(variantIdInputId);
            variantInput && (variantInput.value = '');
            selectButton && (selectButton.innerHTML = `
                <div class="model-select-button__placeholder">Select option</div>
                <div class="model-select-button__dropdown-icon">${_dropdownSvg}</div>
            `);
            selectedProduct = null;
            hiddenInput?.dispatchEvent(new Event('change', { bubbles: true }));
        }

        function updateButtonDisplay() {
            if (!selectButton || !selectedProduct) return;
            const qty = parseInt(document.querySelector('input[name="quantity"]')?.value) || 1;
            const img = selectedProduct.image ? `<img src="${selectedProduct.image}" alt="${selectedProduct.title}">` : '';
            let price = `[+${selectedProduct.priceFormatted} each]`;
            if (qty > 1) {
                const total = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((selectedProduct.price * qty) / 100);
                price += `<div class="model-select-button__total">(${qty} × ${selectedProduct.priceFormatted} = ${total})</div>`;
            }
            selectButton.innerHTML = `
                <div class="model-select-button__image">${img}</div>
                <div class="model-select-button__info">
                    <div class="model-select-button__title-group">
                        <div class="model-select-button__title">${selectedProduct.title}</div>
                        <div class="model-select-button__vendor">${selectedProduct.vendor}</div>
                    </div>
                    <div class="model-select-button__price">${price}</div>
                </div>
                <div class="model-select-button__dropdown-icon">${_dropdownSvg}</div>
            `;
        }

        function renderOptions(searchFilter = '') {
            const brand = modalBrandSelect?.value || '';
            const filtered = products.filter(p => {
                if (brand && p.vendor !== brand) return false;
                if (filterProduct && !filterProduct(p)) return false;
                if (searchFilter) {
                    const s = searchFilter.toLowerCase();
                    return p.title.toLowerCase().includes(s) || p.vendor.toLowerCase().includes(s);
                }
                return true;
            });

            if (!filtered.length) {
                optionsList.innerHTML = '<div class="no-results">No products found</div>';
                return;
            }

            optionsList.innerHTML = filtered.map(p => `
                <div class="model-option ${optionClass}" data-title="${_escapeHtmlAttribute(p.title)}">
                    <div class="model-option__image">${p.image ? `<img src="${_escapeHtmlAttribute(p.image)}" alt="${_escapeHtmlAttribute(p.title)}">` : '<div class="no-image">No image</div>'}</div>
                    <div class="model-option__info">
                        <div class="model-option__title-group">
                            <div class="model-option__title">${p.title}</div>
                            <div class="model-option__vendor">${p.vendor}</div>
                        </div>
                        <div class="model-option__price">[+${p.priceFormatted}]</div>
                    </div>
                </div>
            `).join('');

            optionsList.querySelectorAll(`.${optionClass}`).forEach(el => {
                el.addEventListener('click', () => selectProduct(el.getAttribute('data-title')));
            });
        }

        function selectProduct(title) {
            selectedProduct = products.find(p => p.title === title);
            if (!selectedProduct) return;
            hiddenInput && (hiddenInput.value = selectedProduct.title);
            const variantInput = document.getElementById(variantIdInputId);
            variantInput && (variantInput.value = selectedProduct.variantId || '');
            updateButtonDisplay();
            window.closeModal(modalId);
            hiddenInput?.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Brand sync with clear on change
        if (brandSelect && modalBrandSelect) {
            brandSelect.addEventListener('change', () => {
                modalBrandSelect.value = brandSelect.value;
                clearSelection();
                renderOptions();
            });
            modalBrandSelect.addEventListener('change', () => {
                brandSelect.value = modalBrandSelect.value;
                brandSelect.dispatchEvent(new Event('change', { bubbles: true }));
                clearSelection();
                renderOptions();
            });
        } else if (modalBrandSelect) {
            // Handle modal-only brand filter (e.g., putter shafts)
            modalBrandSelect.addEventListener('change', () => {
                clearSelection();
                renderOptions();
            });
        }

        searchInput?.addEventListener('input', () => renderOptions(searchInput.value));

        selectButton?.addEventListener('click', () => {
            modalBrandSelect && brandSelect && (modalBrandSelect.value = brandSelect.value);
            onFilterChange?.();
            renderOptions();
            searchInput && (searchInput.value = '');
            setTimeout(() => searchInput?.focus(), 100);
        });

        document.querySelector('input[name="quantity"]')?.addEventListener('change', () => {
            selectedProduct && updateButtonDisplay();
        });

        renderOptions();
        return { clearSelection, renderOptions, getProducts: () => products };
    }

    // ============================================================================
    // EXPORT TO WINDOW
    // ============================================================================

    window.clubCustomizationUtils = {
        // Putter length utilities
        generatePutterLengths,
        putterLengths,

        // Lie angle utilities
        generateLieAngles,
        lieAngles,

        // DOM utilities
        _populateSelectOptions,
        _escapeHtmlAttribute,
        _dropdownSvg,

        // Data fetching utilities
        fetchCollectionFeed,
        fetchVariantOverrideProducts,
        convertVariantsToProducts,

        // Model selector
        initModelSelector
    };
})();
